import type { ReactNode } from 'react';
import type { CashReceiptDetail } from '@erp/rpc';
import { T, useGT } from 'gt-react';
import { formatPaymentType } from './cashReceiptFormat';

interface CashReceiptBankSectionProps {
  receipt: CashReceiptDetail;
}

function BankRow({ label, value }: { label: ReactNode; value: string | null }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm break-words">{value ?? '—'}</span>
    </div>
  );
}

/** Render an extracted-row value (string/number/bool/null/object) for display. */
function formatRawValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

export function CashReceiptBankSection({
  receipt,
}: CashReceiptBankSectionProps) {
  const gt = useGT();
  const { bankTransaction } = receipt;

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <T context="AR cash receipt, section heading for linked bank transaction">
          Bank Transaction
        </T>
      </p>

      {bankTransaction ? (
        <div className="space-y-2">
          {receipt.originSystem === 'famous' && receipt.originId && (
            <BankRow
              label={gt('Famous Receipt No.', {
                context:
                  'bank transaction field label, the receipt number in the Famous accounting system',
              })}
              value={receipt.originId}
            />
          )}
          <BankRow
            label={gt('Type', { context: 'bank transaction field label' })}
            value={formatPaymentType(bankTransaction.type, gt)}
          />
          <BankRow
            label={gt('Account', { context: 'bank transaction field label' })}
            value={bankTransaction.accountId}
          />
          <BankRow
            label={gt('Reference No.', {
              context:
                'bank transaction field label, check number or EFT trace number',
            })}
            value={bankTransaction.referenceNumber}
          />
          {bankTransaction.depositNumber && (
            <BankRow
              label={gt('Deposit Slip', {
                context:
                  'bank transaction field label, deposit slip number several checks can share',
              })}
              value={bankTransaction.depositNumber}
            />
          )}
          <BankRow
            label={gt('Merchant Name', {
              context: 'bank transaction field label',
            })}
            value={bankTransaction.merchantName}
          />
          <BankRow
            label={gt('Description', {
              context: 'bank transaction field label',
            })}
            value={bankTransaction.description}
          />
          {bankTransaction.bankMemo && (
            <BankRow
              label={gt('Bank memo', {
                context:
                  'bank transaction field label, the free-text memo the payer sent with the payment',
              })}
              value={bankTransaction.bankMemo}
            />
          )}
          {bankTransaction.bankMemoVerdict && (
            <BankRow
              label={gt('Memo verdict', {
                context:
                  'bank transaction field label, whether the workflow judged the bank memo to say what the payment covers',
              })}
              value={
                bankTransaction.bankMemoVerdict.explainsPayment
                  ? gt('Explains the payment: {reason}', {
                      reason: bankTransaction.bankMemoVerdict.reason,
                    })
                  : gt('Does not explain the payment: {reason}', {
                      reason: bankTransaction.bankMemoVerdict.reason,
                    })
              }
            />
          )}

          {bankTransaction.extractedRow &&
            Object.keys(bankTransaction.extractedRow).length > 0 && (
              <div className="space-y-2 border-t pt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <T context="AR cash receipt, heading for the raw row extracted from the bank statement">
                    Extracted from statement
                  </T>
                </p>
                {Object.entries(bankTransaction.extractedRow).map(
                  ([key, value]) => (
                    <BankRow
                      key={key}
                      label={key}
                      value={formatRawValue(value)}
                    />
                  ),
                )}
              </div>
            )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          <T context="AR cash receipt when no bank transaction is linked">
            No bank data available
          </T>
        </p>
      )}
    </div>
  );
}
